import React, { useId, useState } from 'react';
import Icon from "../icons/Icon";
import Modal from '../modals/Modal';
import EmployeePicker from '../EmployeePicker';
import { format } from 'date-fns';
import { expandableRowProps } from '../../utils/expandableRow';
import { parsePeriodToDate, toYMDString } from '../../utils/periodDate';
import { formatPeso as formatCurrency } from '../../utils/currency';
import { calculateEmployeeFinancials as calcFinancials } from '../../utils/payrollCalculations';
import {
  validateAmount, validateTenure, runDateClamp,
  EARNING_CATEGORIES, DEDUCTION_CATEGORIES, LEGACY_CATEGORIES,
} from '../../services/adjustmentService';
import { useToast } from '../../contexts/ToastContext';
import '../../styles/components/manual-adjustments.css';

const ManualAdjustments = ({ employees, periodStart, periodEnd, onAddAdjustment, onEditAdjustment, onRemoveAdjustment, pendingOutsideRun = [] }) => {
  const toast = useToast();
  // Form state
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [adjustmentType, setAdjustmentType] = useState('earning');
  const [adjustmentCategory, setAdjustmentCategory] = useState('');
  const [adjustmentAmount, setAdjustmentAmount] = useState('');
  const [adjustmentDescription, setAdjustmentDescription] = useState('');
  // C11: explicit effective date, defaulted to the run's periodEnd and clamped
  // to the run's period ∩ the selected employee's tenure (C24).
  const [adjustmentDate, setAdjustmentDate] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const formTitleId = useId();
  const confirmTitleId = useId();
  const [showAddForm, setShowAddForm] = useState(false);

  // Edit and delete state
  const [editingAdjustment, setEditingAdjustment] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  // Accordion: which employee group is expanded (only one at a time; null = all collapsed)
  const [expandedEmpId, setExpandedEmpId] = useState(null);

  // Outside-run notice: collapsed to 3 entries by default (test-impl S9 —
  // 20+ bullets would bury the Adjustments step; the count line keeps the loss visible).
  const [showAllOutside, setShowAllOutside] = useState(false);

  // Form validation errors render INLINE in the modal, matching the
  // AdjustmentsPage modal (test-impl S13 — a toast lands far from the form).
  const [formError, setFormError] = useState(null);

  // Default the date to periodEnd whenever the form opens without one (C11).
  React.useEffect(() => {
    if (showAddForm && !adjustmentDate) {
      setAdjustmentDate(toYMDString(periodEnd));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAddForm]);

  // Categories — the shared C11 lists from adjustmentService (impl-review #10:
  // local copies drift silently; the service is the single source).
  const categories = adjustmentType === 'earning' ? EARNING_CATEGORIES : DEDUCTION_CATEGORIES;

  // Quick templates (4 only)
  const templates = [
    { type: 'earning', category: 'allowance', description: 'Transportation Allowance', icon: 'car', name: 'Transportation', label: 'Allowance' },
    { type: 'earning', category: 'allowance', description: 'Meal Allowance', icon: 'utensils', name: 'Meal', label: 'Allowance' },
    { type: 'earning', category: 'bonus', description: 'Performance Bonus', icon: 'star', name: 'Performance', label: 'Bonus' },
    { type: 'deduction', category: 'cash_advance', description: 'Cash Advance Repayment', icon: 'banknote', name: 'Cash Advance', label: 'Deduction' }
  ];

  // Calculate financial values for an employee using shared utility
  const calculateEmployeeFinancials = (employee) => {
    const result = calcFinancials(employee);
    const availableBalance = result.net;

    return {
      gross: result.gross,
      govtDeductions: result.totalDeductions,
      loans: result.totalLoans, // PRD_LoansManagement C8f — so Available Balance still foots
      manualEarnings: result.breakdown.adjustments.manualEarnings,
      manualDeductions: result.breakdown.adjustments.manualDeductions,
      netAdjustments: result.netAdjustments,
      availableBalance,
      hasNegativeBalance: availableBalance < 0
    };
  };

  // Get employees with negative balance
  const getEmployeesWithNegativeBalance = () => {
    return employees.filter(emp => {
      const financials = calculateEmployeeFinancials(emp);
      return financials.hasNegativeBalance;
    });
  };

  const employeesWithNegativeBalance = getEmployeesWithNegativeBalance();
  const hasAnyNegativeBalance = employeesWithNegativeBalance.length > 0;

  // Get adjustments grouped by employee
  const getAdjustmentsByEmployee = () => {
    const grouped = {};

    employees.forEach(emp => {
      const empId = emp.employeeId || emp.id;
      const empAdjustments = [];

      if (emp.earnings?.adjustments?.length > 0) {
        emp.earnings.adjustments.forEach((adj, idx) => {
          empAdjustments.push({
            ...adj,
            adjustmentIndex: idx,
            type: 'earning'
          });
        });
      }

      if (emp.deductions?.adjustments?.length > 0) {
        emp.deductions.adjustments.forEach((adj, idx) => {
          empAdjustments.push({
            ...adj,
            adjustmentIndex: idx,
            type: 'deduction'
          });
        });
      }

      if (empAdjustments.length > 0) {
        grouped[empId] = {
          employee: emp,
          adjustments: empAdjustments
        };
      }
    });

    return grouped;
  };

  const adjustmentsByEmployee = getAdjustmentsByEmployee();

  // Get total count of all adjustments
  const getTotalAdjustmentCount = () => {
    return Object.values(adjustmentsByEmployee).reduce(
      (sum, group) => sum + group.adjustments.length, 0
    );
  };

  // Get total earnings, deductions, and net
  const getTotals = () => {
    let earnings = 0;
    let deductions = 0;

    Object.values(adjustmentsByEmployee).forEach(group => {
      group.adjustments.forEach(adj => {
        if (adj.type === 'earning') {
          earnings += adj.amount;
        } else {
          deductions += adj.amount;
        }
      });
    });

    return { earnings, deductions, net: earnings - deductions };
  };

  const totals = getTotals();
  const totalCount = getTotalAdjustmentCount();

  const resetForm = () => {
    setSelectedEmployee(null);
    setAdjustmentType('earning');
    setAdjustmentCategory('');
    setAdjustmentAmount('');
    setAdjustmentDescription('');
    setAdjustmentDate('');
    setShowAddForm(false);
    setEditingAdjustment(null);
    setFormError(null);
  };

  // The C24 Adjustments-step clamp: the run's period, narrowed to the selected
  // employee's tenure — extracted to adjustmentService (Tier-1 tested).
  const dateClamp = runDateClamp(periodStart, periodEnd, selectedEmployee?.employment);

  const handleSubmitAdjustment = async () => {
    setFormError(null); // fresh attempt (errors render INLINE in the modal — test-impl S13)
    // Description is optional, as on the Adjustments page (founder, /test-impl 2026-10-05).
    if (!selectedEmployee || !adjustmentCategory || !adjustmentAmount || !adjustmentDate) {
      setFormError('Please fill in all required fields');
      return;
    }

    const amount = Math.round(parseFloat(adjustmentAmount) * 100) / 100;
    const amountError = validateAmount(isNaN(amount) ? NaN : amount);
    if (amountError) {
      setFormError(amountError);
      return;
    }
    // C12a against the run employee's current dates (the service re-checks too).
    const tenureError = validateTenure(adjustmentDate, selectedEmployee.employment || {});
    if (tenureError) {
      setFormError(tenureError);
      return;
    }
    // C13 cycle bound (impl-review #8): the picker is clamped, but a TYPED date
    // can walk past min/max — and an out-of-period entry would embed fine, then
    // hard-abort the whole save server-side. Catch it at entry instead.
    const dateYMD = toYMDString(adjustmentDate);
    const pStartYMD = toYMDString(periodStart);
    const pEndYMD = toYMDString(periodEnd);
    if (dateYMD < pStartYMD || dateYMD > pEndYMD) {
      setFormError(`Date must be inside this run's period (${pStartYMD} to ${pEndYMD}).`);
      return;
    }

    const adjustment = {
      type: adjustmentType,
      category: adjustmentCategory,
      description: adjustmentDescription,
      amount: amount,
      effectiveDate: adjustmentDate,
      // Preserve the standalone-doc link on edit (C11/C14 write-through).
      ...(editingAdjustment?.adjustment?.sourceId ? { sourceId: editingAdjustment.adjustment.sourceId } : {}),
      addedAt: editingAdjustment ? editingAdjustment.adjustment.addedAt : new Date(),
      updatedAt: editingAdjustment ? new Date() : null,
      addedBy: editingAdjustment ? editingAdjustment.adjustment.addedBy : 'current_user'
    };

    const employeeId = selectedEmployee.employeeId || selectedEmployee.id;

    setIsSaving(true);
    try {
      if (editingAdjustment) {
        await onEditAdjustment(
          employeeId,
          editingAdjustment.adjustmentIndex,
          editingAdjustment.type,
          adjustment
        );
      } else {
        await onAddAdjustment(employeeId, adjustment);
      }
      // Open this employee's accordion so the new/edited row is visible right away.
      setExpandedEmpId(employeeId);
      resetForm();
    } catch (err) {
      // The write-through failed (validation / rules / network) — nothing was
      // added to the run, so surface it inline and leave the form open to fix.
      setFormError(err.message || 'Could not save the adjustment.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditClick = (employeeId, adjustment) => {
    const employee = employees.find(e => (e.employeeId || e.id) === employeeId);

    setEditingAdjustment({
      employeeId,
      adjustmentIndex: adjustment.adjustmentIndex,
      type: adjustment.type,
      adjustment
    });

    setSelectedEmployee(employee);
    setAdjustmentType(adjustment.type);
    setAdjustmentCategory(adjustment.category);
    setAdjustmentAmount(adjustment.amount.toString());
    setAdjustmentDescription(adjustment.description);
    setAdjustmentDate(toYMDString(adjustment.effectiveDate) || toYMDString(periodEnd));
    setShowAddForm(true);
  };

  const handleDeleteClick = (employeeId, adjustment) => {
    const employee = employees.find(e => (e.employeeId || e.id) === employeeId);
    setShowDeleteConfirm({
      employeeId,
      adjustmentIndex: adjustment.adjustmentIndex,
      type: adjustment.type,
      adjustment,
      employeeName: employee?.employeeName || employee?.personalInfo?.fullName
    });
  };

  // C19 — two distinct acts. 'remove' drops the entry from THIS run only (it
  // stays Pending on the Adjustments page for a future cycle); 'void'
  // soft-deletes the standalone record itself (C18).
  const confirmDelete = async (action) => {
    if (!showDeleteConfirm) return;
    try {
      await onRemoveAdjustment(
        showDeleteConfirm.employeeId,
        showDeleteConfirm.adjustmentIndex,
        showDeleteConfirm.type,
        action
      );
      setShowDeleteConfirm(null);
    } catch (err) {
      toast.error(err.message || 'Could not remove the adjustment.');
    }
  };

  const handleTemplateClick = (template) => {
    setAdjustmentType(template.type);
    setAdjustmentCategory(template.category);
    setAdjustmentDescription(template.description);
    setShowAddForm(true);
  };

  // Utility functions
  const getCategoryLabel = (category) => {
    const allCategories = [...EARNING_CATEGORIES, ...DEDUCTION_CATEGORIES, ...LEGACY_CATEGORIES];
    return allCategories.find(c => c.value === category)?.label || category;
  };

  const getInitials = (name) => {
    if (!name) return '??';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getEmployeeDepartment = (employee) => {
    return employee.employment?.department || employee.personalInfo?.department || '';
  };


  return (
    <div className="manual-adjustments">
      <div className="adjustments-content">
        {/* Header */}
        <div className="adjustments-toprow">
          <div className="pw-header">
            <h2 className="pw-header__title">Manual Adjustments</h2>
            {periodStart && periodEnd && (
              <span className="pw-header__badge">
                {format(parsePeriodToDate(periodStart), 'MMM d, yyyy')} – {format(parsePeriodToDate(periodEnd), 'MMM d, yyyy')}
              </span>
            )}
          </div>
          <button
            className="ma-btn ma-btn-primary ma-btn-add"
            onClick={() => setShowAddForm(true)}
          >
            + Add Adjustment
          </button>
        </div>

      {/* C13 — pending in-cycle entries whose employee is NOT in this run.
          Step 2 excludes employees for reasons the clerk can't predict; without
          this notice the entry silently stays Pending with nobody the wiser. */}
      {pendingOutsideRun.length > 0 && (
        <div
          role="status"
          style={{
            background: 'var(--ui-accent-tint)',
            border: '1px solid var(--ui-accent)',
            borderRadius: '8px',
            padding: '0.75rem 1rem',
            margin: '0 0 1rem 0',
            fontSize: '0.8125rem',
            color: 'var(--ui-warning-text)',
          }}
        >
          <strong>
            {pendingOutsideRun.length} pending adjustment{pendingOutsideRun.length === 1 ? '' : 's'} dated in
            this period belong{pendingOutsideRun.length === 1 ? 's' : ''} to employees not in this run.
          </strong>{' '}
          <button
            type="button"
            onClick={() => setShowAllOutside((v) => !v)}
            style={{
              background: 'none', border: 'none', padding: 0,
              color: 'var(--ui-warning-text)', fontWeight: 600, fontSize: '0.8125rem',
              textDecoration: 'underline', cursor: 'pointer',
            }}
          >
            {showAllOutside ? 'Show less' : 'Show more'}
          </button>
          {showAllOutside && (
            <>
              <ul style={{ margin: '0.375rem 0 0 1.125rem', padding: 0 }}>
                {pendingOutsideRun.map((p) => (
                  <li key={p.id}>
                    EMP-{p.employeeId} — {p.description || getCategoryLabel(p.category)} ({p.type === 'earning' ? '+' : '−'}₱
                    {(p.amount || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}, {p.effectiveDate})
                  </li>
                ))}
              </ul>
              <div style={{ marginTop: '0.375rem' }}>
                They stay Pending — they'll be picked up by a payroll run that includes the employee and covers their date. Or re-date or delete the entry on the Adjustments page.
              </div>
            </>
          )}
        </div>
      )}

      {/* Current Adjustments - Grouped by Employee */}
      <div className="ma-list-section">
        <div className="ma-list-header">
          <h3>Current Adjustments</h3>
          {totalCount > 0 && (
            <span className="ma-count-badge">{totalCount}</span>
          )}
        </div>

        {Object.keys(adjustmentsByEmployee).length === 0 ? (
          <div className="ma-empty-state">
            <span className="ma-empty-icon"><Icon name="clipboard-list" size={32} /></span>
            <h4>No adjustments yet</h4>
            <p>Add manual earnings or deductions to customize employee payroll</p>
            <button
              className="ma-btn ma-btn-secondary"
              onClick={() => setShowAddForm(true)}
            >
              Add First Adjustment
            </button>
          </div>
        ) : (
          <div className="pw-table-scroll">
            <table className="pw-table pw-table--adjustments">
              <thead>
                <tr>
                  <th className="th-employee">Employee</th>
                  <th className="th-id">ID</th>
                  <th className="th-department">Department</th>
                  <th className="th-gross">Gross Income</th>
                  <th className="th-deductions">Gov't Contributions</th>
                  <th className="th-loans">Loans</th>
                  <th className="th-adjustments">Adjustments</th>
                  <th className="th-balance">Available Balance</th>
                </tr>
              </thead>
              <tbody>
            {Object.entries(adjustmentsByEmployee).map(([empId, group]) => {
              const { employee, adjustments } = group;
              const financials = calculateEmployeeFinancials(employee);
              const employeeName = employee.employeeName || employee.personalInfo?.fullName || 'Unknown';
              const employeeCode = employee.employeeCode || employee.employeeId;
              const department = getEmployeeDepartment(employee);
              const isExpanded = expandedEmpId === empId;

              // Calculate group totals
              const groupEarnings = adjustments.filter(a => a.type === 'earning').reduce((sum, a) => sum + a.amount, 0);
              const groupDeductions = adjustments.filter(a => a.type === 'deduction').reduce((sum, a) => sum + a.amount, 0);
              const groupNet = groupEarnings - groupDeductions;

              return (
                <React.Fragment key={empId}>
                  <tr
                    className={`employee-row${isExpanded ? ' expanded' : ''}${financials.hasNegativeBalance ? ' negative-balance' : ''}`}
                    {...expandableRowProps(isExpanded, () => setExpandedEmpId(prev => (prev === empId ? null : empId)))}
                  >
                    <td className="pw-name-cell">
                      <div className="pw-name-cell__inner">
                        <div className="pw-avatar">{getInitials(employeeName)}</div>
                        <span className="pw-name-cell__text">{employeeName}</span>
                        <span className={`pw-chevron ${isExpanded ? 'expanded' : ''}`}>&#9660;</span>
                      </div>
                    </td>
                    <td className="td-id">{employeeCode}</td>
                    <td className="td-department">{department || '-'}</td>
                    <td className="td-currency">{formatCurrency(financials.gross)}</td>
                    <td className="td-currency">{formatCurrency(financials.govtDeductions)}</td>
                    <td className="td-currency">{formatCurrency(financials.loans)}</td>
                    <td className="td-currency td-adjustments">
                      {financials.netAdjustments !== 0 ? (
                        <span className={financials.netAdjustments > 0 ? 'positive' : 'negative'}>
                          {financials.netAdjustments > 0 ? '+' : ''}{formatCurrency(financials.netAdjustments)}
                        </span>
                      ) : (
                        formatCurrency(0)
                      )}
                    </td>
                    <td className={`td-currency td-balance${financials.availableBalance < 0 ? ' negative' : ''}`}>
                      {formatCurrency(financials.availableBalance)}
                    </td>
                  </tr>

                  {isExpanded && (
                  <tr className="ma-expansion-row">
                  <td colSpan="8">
                  <div className="ma-expansion-content">
                  <table className="ma-adjustments-table">
                    <thead>
                      <tr>
                        <th>Type</th>
                        <th>Category</th>
                        <th>Description</th>
                        <th className="amount-col">Amount</th>
                        <th className="actions-col">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {adjustments.map((adjustment, index) => (
                        <tr key={index}>
                          <td>
                            <span className={`ma-badge ma-badge-${adjustment.type}`}>
                              {adjustment.type === 'earning' ? '+ Earning' : '− Deduction'}
                            </span>
                          </td>
                          <td>{getCategoryLabel(adjustment.category)}</td>
                          <td className="ma-description">{adjustment.description}</td>
                          <td className="amount-col">
                            <span className={`ma-amount-value ${adjustment.type}`}>
                              {adjustment.type === 'earning' ? '+' : '−'}{formatCurrency(adjustment.amount)}
                            </span>
                          </td>
                          <td>
                            <div className="ma-actions-cell">
                              <button
                                className="ma-btn-icon ma-btn-edit"
                                onClick={() => handleEditClick(empId, adjustment)}
                                title="Edit"
                              >
                                <Icon name="pencil" size={16} aria-label="Edit" />
                              </button>
                              <button
                                className="ma-btn-icon ma-btn-delete"
                                onClick={() => handleDeleteClick(empId, adjustment)}
                                title="Remove"
                              >
                                ×
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <div className="ma-group-totals">
                    <div className="ma-group-total-item">
                      <span className="ma-group-total-label">Earnings:</span>
                      <span className="ma-group-total-value earning">+{formatCurrency(groupEarnings)}</span>
                    </div>
                    <div className="ma-group-total-item">
                      <span className="ma-group-total-label">Deductions:</span>
                      <span className="ma-group-total-value deduction">−{formatCurrency(groupDeductions)}</span>
                    </div>
                    <div className="ma-group-total-item">
                      <span className="ma-group-total-label">Net:</span>
                      <span className={`ma-group-total-value ${groupNet >= 0 ? 'net' : 'net negative'}`}>
                        {groupNet >= 0 ? '+' : ''}{formatCurrency(groupNet)}
                      </span>
                    </div>
                  </div>

                  {financials.hasNegativeBalance && (
                    <div className="ma-negative-warning">
                      <span className="ma-warning-icon"><Icon name="alert-triangle" size={16} /></span>
                      <span className="ma-warning-text">
                        Negative balance! Reduce loans or deductions by {formatCurrency(Math.abs(financials.availableBalance))} to proceed.
                      </span>
                    </div>
                  )}
                  </div>
                  </td>
                  </tr>
                  )}
                </React.Fragment>
              );
            })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Quick Templates */}
      <div className="ma-templates">
        <div className="ma-templates-header">
          <h3>Quick Templates</h3>
          <p className="ma-templates-desc">Select a template to quickly add common adjustments</p>
        </div>
        <div className="ma-templates-grid">
          {templates.map((template, index) => (
            <button
              key={index}
              className={`ma-template-card ${template.type}`}
              onClick={() => handleTemplateClick(template)}
            >
              <span className="ma-template-icon"><Icon name={template.icon} size={18} /></span>
              <span className="ma-template-text">
                <span className="ma-template-name">{template.name}</span>
                <span className="ma-template-type">{template.label}</span>
              </span>
            </button>
          ))}
        </div>
      </div>

        {/* Totals footer (pinned) — replaces the old top summary cards */}
        <div className="pw-footer">
          <div className="pw-footer__stats">
            <div className="pw-footer__stat">
              <span className="pw-footer__stat-label">Total Adjustments</span>
              <span className="pw-footer__stat-value">{totalCount}</span>
            </div>
            <div className="pw-footer__stat">
              <span className="pw-footer__stat-label">Total Earnings</span>
              <span className="pw-footer__stat-value">{formatCurrency(totals.earnings)}</span>
            </div>
            <div className="pw-footer__stat">
              <span className="pw-footer__stat-label">Manual Deductions</span>
              <span className="pw-footer__stat-value">{formatCurrency(totals.deductions)}</span>
            </div>
            <div className="pw-footer__stat pw-footer__stat--total">
              <span className="pw-footer__stat-label">Net Impact</span>
              <span className="pw-footer__stat-value">{formatCurrency(totals.net)}</span>
            </div>
          </div>
          {hasAnyNegativeBalance && (
            <span className="pw-footer__hint">
              <Icon name="alert-triangle" size={14} aria-hidden="true" /> {employeesWithNegativeBalance.length} employee{employeesWithNegativeBalance.length > 1 ? 's have' : ' has'} negative balance
            </span>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showAddForm && (
        <Modal isOpen onClose={resetForm} busy={isSaving} labelledBy={formTitleId} className="ma-modal">
            <div className="ma-modal-header">
              <div className="ma-modal-title">
                <span className="ma-modal-icon"><Icon name="file-pen" size={18} /></span>
                <h3 id={formTitleId}>{editingAdjustment ? 'Edit Adjustment' : 'Add Manual Adjustment'}</h3>
              </div>
            </div>

            <div className="ma-modal-body">
              {/* Employee Selection */}
              <div className="ma-form-group">
                <label>Employee *</label>

                <EmployeePicker
                  employees={employees}
                  value={selectedEmployee}
                  onChange={setSelectedEmployee}
                  locked={Boolean(editingAdjustment)}
                />
              </div>

              {/* Type Selection */}
              <div className="ma-form-group">
                <label>Type *</label>
                <div className="ma-radio-group">
                  <label className={`ma-radio-option ${adjustmentType === 'earning' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      value="earning"
                      checked={adjustmentType === 'earning'}
                      onChange={(e) => { setAdjustmentType(e.target.value); setAdjustmentCategory(''); }}
                    />
                    <div className="ma-radio-content">
                      <span className="ma-radio-icon earning">+</span>
                      <span className="ma-radio-label">Earning</span>
                    </div>
                  </label>
                  <label className={`ma-radio-option ${adjustmentType === 'deduction' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      value="deduction"
                      checked={adjustmentType === 'deduction'}
                      onChange={(e) => { setAdjustmentType(e.target.value); setAdjustmentCategory(''); }}
                    />
                    <div className="ma-radio-content">
                      <span className="ma-radio-icon deduction">−</span>
                      <span className="ma-radio-label">Deduction</span>
                    </div>
                  </label>
                </div>
              </div>

              {/* Category + Amount (one row; Amount wraps below on narrow widths) */}
              <div className="ma-form-row">
                <div className="ma-form-group">
                  <label>Category *</label>
                  <select
                    className="ma-select"
                    value={adjustmentCategory}
                    onChange={(e) => setAdjustmentCategory(e.target.value)}
                  >
                    <option value="">Select category...</option>
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>

                <div className="ma-form-group">
                  <label>Amount *</label>
                  <div className="ma-amount-input">
                    <span className="ma-currency">₱</span>
                    <input
                      type="number"
                      className="ma-input"
                      placeholder="0.00"
                      value={adjustmentAmount}
                      onChange={(e) => setAdjustmentAmount(e.target.value)}
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>
              </div>

              {/* Effective date (C11/C24) — clamped to the run's period ∩ the
                  selected employee's tenure; defaults to periodEnd. */}
              <div className="ma-form-group">
                <label>Effective Date *</label>
                <input
                  type="date"
                  className="ma-input"
                  value={adjustmentDate}
                  min={dateClamp.min}
                  max={dateClamp.max}
                  onChange={(e) => setAdjustmentDate(e.target.value)}
                />
              </div>

              {/* Description */}
              <div className="ma-form-group">
                <label>Description</label>
                <textarea
                  className="ma-textarea"
                  placeholder="Enter description for this adjustment..."
                  value={adjustmentDescription}
                  onChange={(e) => setAdjustmentDescription(e.target.value)}
                  rows="3"
                />
              </div>

            </div>

            {formError && (
              <div
                role="alert"
                style={{
                  background: 'var(--ui-danger-tint)',
                  color: 'var(--ui-danger-text)',
                  borderRadius: '8px',
                  padding: '0.625rem 0.875rem',
                  margin: '0 1.5rem 0.75rem',
                  fontSize: '0.8125rem',
                }}
              >
                {formError}
              </div>
            )}

            {/* Actions — pinned modal footer (stays visible while the body scrolls) */}
            <div className="ma-form-actions">
              <button className="ma-btn ma-btn-secondary" onClick={resetForm} disabled={isSaving}>Cancel</button>
              <button
                className="ma-btn ma-btn-primary"
                onClick={handleSubmitAdjustment}
                disabled={isSaving || !selectedEmployee || !adjustmentCategory || !adjustmentAmount || !adjustmentDate}
              >
                {isSaving ? 'Saving…' : editingAdjustment ? 'Save Changes' : 'Add Adjustment'}
              </button>
            </div>
        </Modal>
      )}

      {/* Delete Confirmation Modal — no busy state exists for the remove, so it
          stays dismissible as before (PRD_ModalBehavior C4 table) */}
      {showDeleteConfirm && (
        <Modal isOpen onClose={() => setShowDeleteConfirm(null)} labelledBy={confirmTitleId} className="ma-confirm-dialog">
            <div className="ma-confirm-icon"><Icon name="trash-2" size={28} /></div>
            <div className="ma-confirm-title" id={confirmTitleId}>Remove Adjustment?</div>
            <div className="ma-confirm-message">
              {showDeleteConfirm.adjustment.sourceId ? (
                <>
                  <strong>Remove from this run</strong> takes the <strong>{showDeleteConfirm.adjustment.description || getCategoryLabel(showDeleteConfirm.adjustment.category)}</strong> (
                  {showDeleteConfirm.type === 'earning' ? '+' : '−'}{formatCurrency(showDeleteConfirm.adjustment.amount)})
                  adjustment for <strong>{showDeleteConfirm.employeeName}</strong> out of this payroll run only —
                  it <strong>stays on the Adjustments page as Pending</strong> for a future cycle.{' '}
                  <strong>Delete</strong> discards the adjustment itself (it stays in the audit trail).
                </>
              ) : (
                <>
                  Are you sure you want to remove the <strong>{showDeleteConfirm.adjustment.description || getCategoryLabel(showDeleteConfirm.adjustment.category)}</strong> (
                  {showDeleteConfirm.type === 'earning' ? '+' : '−'}{formatCurrency(showDeleteConfirm.adjustment.amount)})
                  adjustment for <strong>{showDeleteConfirm.employeeName}</strong>? This action cannot be undone.
                </>
              )}
            </div>
            <div className="ma-confirm-actions">
              <button className="ma-btn ma-btn-secondary" onClick={() => setShowDeleteConfirm(null)}>Cancel</button>
              {showDeleteConfirm.adjustment.sourceId && (
                <button className="ma-btn ma-btn-danger" onClick={() => confirmDelete('void')}>Delete Adjustment</button>
              )}
              <button className="ma-btn ma-btn-danger" onClick={() => confirmDelete('remove')}>
                {showDeleteConfirm.adjustment.sourceId ? 'Remove from This Run' : 'Remove Adjustment'}
              </button>
            </div>
        </Modal>
      )}
    </div>
  );
};

export default ManualAdjustments;
